import { useRef, useEffect, useState } from 'react';
import { getActiveState } from '../helpers/titreHelper';
import { useGetParams } from './useGetParams';
import { useInterval } from './useInterval';

export const useBroadcast = () => {
  const { user, repeatCount, duration, speedSlow, speedFast } = useGetParams();

  const [currentState, setCurrentState] = useState(null);
  const [isVisible, setIsVisible] = useState(null);
  const [currentMessage, setCurrentMessage] = useState(null);
  const messageAnimationTriggerRef = useRef(false);

  const currentSpeed = useRef(speedSlow);
  const currentType = useRef('F1');

  const lastMessageIdRef = useRef(0);
  const nextMessageIndexRef = useRef(-1);
  const messagesRef = useRef([]);

  const iactiveIdRef = useRef('');

  // задержка интервала смены сообщений
  const [delay, setDelay] = useState(duration / currentSpeed.current);

  // id предыдущего запущенного интерактива
  const prevIdBroadcast = useRef('');
  // флаг первоначальной загрузки
  const isDataSetRef = useRef(null);
  // прошлый массив сообщений, для проверки на добавление сообщений в пустой массив
  const prevMessagesRef = useRef([]);

  // индекс предыдущего соообщения
  const prevIndex = useRef(0);

  const protocol = window?.location.protocol || 'https:';

  // работа от кликера
  const [clicker, setClicker] = useState(false);

  function keyHandler(e) {
    setClicker(e.key);
    if (e.key === 'PageUp') {
      setDelay(null);
    } else if (e.key === 'PageDown') {
      setDelay(duration / speedFast);
    }
  }

  useEffect(() => {
    document.addEventListener('keydown', keyHandler);
    return () => {
      document.removeEventListener('keydown', keyHandler);
    };
  }, []);

  const getBroadcastInfo = async () => {
    try {
      if (!user) {
        return;
      }
      const response = await fetch(
        `${protocol}//api.stream.iactive.pro/titreInfo?user=${user}&from=${lastMessageIdRef.current}&type=1`,
        { cache: 'no-store' },
      );
      const data = await response.json();

      if (!data) return;

      let { messagesList, deletedMessagesIdList, visibility, iactiveId } = data;

      // очистка при остановке\смене интерактива, при остановке iactiveId = ""
      if (iactiveIdRef.current !== iactiveId) {
        if (
          iactiveId &&
          iactiveId !== prevIdBroadcast.current // если === , значит prevIdBroadcast был остановлен и теперь запущен, и не нужно обнулять
        ) {
          messagesRef.current = [];
          setCurrentMessage(null);
          nextMessageIndexRef.current = -1;
        }
      }

      // сохранение id нового запущенного интерактива.
      if (iactiveId && iactiveId !== prevIdBroadcast.current) {
        // Если уже был предыдущий запущенный интерактив, то показать сообщение сразу без задержки.
        if (prevIdBroadcast.current) {
          // console.log('смена интреактива');
          setDelay(0);
        }
        prevIdBroadcast.current = iactiveId;
      }

      // Определяем видимость титра

      if (!visibility && visibility !== isVisible) {
        setIsVisible(false);
        setDelay(null);
        return;
      } else if (visibility && visibility !== isVisible) {
        setIsVisible(true);
        if (delay !== duration / currentSpeed.current) {
          setDelay(duration / currentSpeed.current);
        }
      }

      // Определяем состояние (S1, S2 ...)

      // если никакая стадия не включена
      if (
        data?.statusList[0]?.state &&
        !getActiveState(data?.statusList[0]?.state) &&
        delay !== null
      ) {
        setDelay(null);
      }

      let activeState = getActiveState(data?.statusList[0]?.state);

      // если произошла смена стадии
      if (data?.statusList[0]?.state && activeState !== currentState) {
        setCurrentState(activeState);

        currentType.current = Number(activeState?.slice(1)) <= 4 ? 'F1' : 'F2';

        setClicker(false);
        // текущая скорость переключения сообщений
        currentSpeed.current = ['S2', 'S6'].includes(activeState)
          ? speedSlow
          : ['S3', 'S7'].includes(activeState)
            ? speedFast
            : speedSlow;

        // стадии показа сообщения
        if (['S4', 'S8'].includes(activeState)) {
          setDelay(null);
          return;
        } else {
          if (delay !== duration / currentSpeed.current) {
            if (!currentMessage) {
              setDelay(0);
            } else {
              setDelay(duration / currentSpeed.current);
            }
          }
        }
      }

      if (activeState && ['S4', 'S8'].includes(activeState)) {
        setDelay(null);
      }

      // если список сообщений пустой
      if (
        !messagesList ||
        (Array.isArray(messagesList) && messagesList.length === 0)
      ) {
        // если имеются в списке сообщений удаленные
        if (
          messagesRef.current.some((el) =>
            deletedMessagesIdList.includes(+el.id),
          )
        ) {
          const newState = messagesRef.current.filter(
            (el) => !deletedMessagesIdList.includes(+el.id),
          );

          for (let i = 0; i < messagesRef.current.length; i++) {
            if (
              !newState.find(
                (el) =>
                  nextMessageIndexRef.current &&
                  el.id ===
                    messagesRef.current[nextMessageIndexRef.current]?.id,
              )
            ) {
              if (
                messagesRef.current[nextMessageIndexRef.current] ===
                messagesRef.current[messagesRef.current.length - 1]
              ) {
                nextMessageIndexRef.current--;
                if (nextMessageIndexRef.current < 0) {
                  nextMessageIndexRef.current = 0;
                }
              }
            }
            if (
              i < nextMessageIndexRef.current &&
              !newState.find((el) => el.id === messagesRef.current[i].id)
            ) {
              nextMessageIndexRef.current--;
              if (nextMessageIndexRef.current < 0) {
                nextMessageIndexRef.current = 0;
              }
            }
          }
          messagesRef.current = newState;
        }

        // обнуление прошлого массива сообщений
        if (
          messagesRef.current.length === 0 &&
          prevMessagesRef.current.length !== 0
        ) {
          prevMessagesRef.current = messagesRef.current;
        }

        // сброс интервала, если список пришедших сообщений пустой, и сброс текущего сообщения
        if (
          messagesRef.current.length === 0 &&
          delay !== null &&
          iactiveIdRef.current &&
          iactiveIdRef.current === iactiveId // тот же интерактив
        ) {
          if (currentMessage) {
            setCurrentMessage(null);
          }
          setDelay(null);
        }

        //запоминаем id последнего сообщения, для дальнейших запросов и обнуляем при переключении интерактивов

        if (iactiveIdRef.current && iactiveIdRef.current !== iactiveId) {
          lastMessageIdRef.current = 0;
          iactiveIdRef.current = iactiveId;
          return;
        } else {
          iactiveIdRef.current = iactiveId;
          lastMessageIdRef.current =
            messagesList[0]?.id || lastMessageIdRef.current;
        }

        return;
      }

      // если список сообщений не пустой

      messagesList = messagesList.filter((el) => el.message.content);

      //remove empty audio
      messagesList = messagesList.filter(
        (el) =>
          !(
            (
              Array.isArray(el.message.attachments) &&
              el.message.attachments.length > 0 &&
              typeof el.message.attachments[0] === 'object' &&
              el.message.attachments[0].type === 'audio_msg' &&
              el.message.content === ''
            ) // лишняя проверка, выше фильтр по content
          ),
      );

      const messagesRepeatCount =
        lastMessageIdRef.current === '0' ? repeatCount : null;

      //запоминаем id последнего сообщения, для дальнейших запросов и обнуляем при переключении интерактивов

      if (iactiveIdRef.current && iactiveIdRef.current !== iactiveId) {
        lastMessageIdRef.current = 0;
        iactiveIdRef.current = iactiveId;
        return;
      } else {
        iactiveIdRef.current = iactiveId;
        lastMessageIdRef.current =
          messagesList[0]?.id || lastMessageIdRef.current;
      }

      // парсинг вложения, предзагрузка аватарок
      messagesList.forEach((el) => {
        let { attachments } = el.message;
        if (
          typeof attachments === 'string' &&
          attachments.replace(/&quot;/g, '"') !== ''
        ) {
          attachments = JSON.parse(attachments.replace(/&quot;/g, '"'));
        }

        if (el.message.image) {
          const createImage = (url) => {
            const img = document.createElement('img');
            img.src = url;
            return img;
          };
          const url = el.message.image;
          document.getElementById('img-loader')?.appendChild(createImage(url));
        }
      });

      let result = [];

      // проверка на дубликаты сообщений по первому вложению и по (автору + контент)
      messagesList.forEach((el) => {
        if (
          typeof el.message?.attachments?.[0] === 'object' &&
          el.message?.attachments?.[0]?.url &&
          !messagesRef.current.find(
            (elm) =>
              typeof elm.message.attachments?.[0] === 'object' &&
              typeof el.message.attachments?.[0] === 'object' &&
              elm.message.attachments?.[0]?.url ===
                el.message.attachments?.[0]?.url,
          )
        ) {
          result.push(el);
        } else if (
          !messagesRef.current.find(
            (elm) =>
              elm.message.content === el.message.content &&
              elm.message.author === el.message.author,
          )
        ) {
          result.push(el);
        }
      });

      // добавление сообщений за исключением удаленных
      messagesRef.current = [
        ...messagesRef.current.filter(
          (el) => !deletedMessagesIdList.includes(+el.id),
        ),
        ...result
          .reverse()
          .slice(messagesRepeatCount ? -messagesRepeatCount : 0),
      ].slice(-repeatCount);

      // зануление интервала, если в пустой массив сообщений добавились сообщения и это не первый запуск
      if (
        prevMessagesRef.current.length === 0 &&
        isDataSetRef.current &&
        messagesRef.current.length !== 0
      ) {
        // console.log('add msg to empty array');
        // изменение индекса след сообщения, чтобы не пропустить первое при нулевой задержке
        if (nextMessageIndexRef.current === 0) {
          nextMessageIndexRef.current = -1;
        }
        setDelay(0);
      }

      prevMessagesRef.current = messagesRef.current;
    } catch (error) {
      console.error(error);
    }
  };

  function getRandomInt(min, max, prev) {
    min = Math.ceil(min);
    max = Math.floor(max);
    if (min === max) {
      return 0;
    }
    let res = Math.floor(Math.random() * (max - min) + min);
    if (prev === res && messagesRef.current?.length > 1) {
      return getRandomInt(min, max, prev);
    }
    return res;
  }

  useEffect(() => {
    getBroadcastInfo().then(() => {
      if (messagesRef.current && messagesRef.current.length > 0) {
        //рандомный индекс следующего сообщения, отличный от предыдущего индекса

        let indexRandom = getRandomInt(
          0,
          messagesRef.current.filter(
            (msg) => msg.titrTag === currentType.current,
          ).length,
          prevIndex.current,
        );
        // обновление предыдущего индекса сообщения
        prevIndex.current = indexRandom;

        setCurrentMessage(
          messagesRef.current.filter(
            (msg) => msg.titrTag === currentType.current,
          )[indexRandom],
        );
      }
      // при первом запуске
      isDataSetRef.current = true;
    });
  }, []);

  // Выбор сообщения для показа
  useInterval(
    () => {
      if (
        messagesRef.current &&
        messagesRef.current.length === 0 &&
        currentMessage !== null
      ) {
        setCurrentMessage(null);
      } else if (messagesRef.current && messagesRef.current.length > 0) {
        //рандомный индекс следующего сообщения, отличный от предыдущего индекса
        let indexRandom = getRandomInt(
          0,
          messagesRef.current.filter(
            (msg) => msg.titrTag === currentType.current,
          ).length,
          prevIndex.current,
        );
        // обновление предыдущего индекса сообщения
        prevIndex.current = indexRandom;

        setCurrentMessage(
          messagesRef.current.filter(
            (msg) => msg.titrTag === currentType.current,
          )[prevIndex.current],
        );
        messageAnimationTriggerRef.current =
          !messageAnimationTriggerRef.current;

        // добавление задержки интервалу, после отработки по нулевой задержке (delay === 0)
        if (delay === 0) {
          setDelay(duration / currentSpeed.current);
        }
      }
    },
    currentState && !['S4', 'S8'].includes(currentState) ? delay : null,
  );

  // Получение информации о титре
  useInterval(getBroadcastInfo, 1000);

  return {
    currentState,
    isVisible,
    currentMessage,
    messageAnimationTrigger: messageAnimationTriggerRef.current,
    messages: messagesRef.current?.filter(
      (msg) => msg.titrTag === currentType.current,
    ),
    clicker,
    currentType: currentType.current,
  };
};
